import { useEffect, useRef, useState } from "react";
import { Image as ImageIcon, Upload, Trash2, Loader2, Sparkles, ArrowRight, Camera, Pencil, Check, X } from "lucide-react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
    uploadImage, listImages, getImage, deleteImage, updateImage,
    fetchBillingStatus, formatApiError,
} from "@/lib/api";
import { useGardens } from "@/contexts/GardenContext";
import UpgradeBanner, { upgradeMessageFrom } from "@/components/UpgradeBanner";
import DiagnoseDialog from "@/components/DiagnoseDialog";

export default function Images() {
    const { activeGardenId } = useGardens();
    const [images, setImages] = useState([]);
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [caption, setCaption] = useState("");
    const [previewUri, setPreviewUri] = useState(null);
    const [upgradeMsg, setUpgradeMsg] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editCaption, setEditCaption] = useState("");
    const [savingCaption, setSavingCaption] = useState(false);
    const [diagnoseTarget, setDiagnoseTarget] = useState(null);  // {dataUri, filename}
    const fileRef = useRef(null);

    const openDiagnose = async (img) => {
        try {
            const full = await getImage(img.id);
            setDiagnoseTarget({
                dataUri: full.data_uri,
                filename: img.filename || "photo.jpg",
            });
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const startEdit = (img) => {
        setEditingId(img.id);
        setEditCaption(img.caption || "");
    };
    const cancelEdit = () => {
        setEditingId(null);
        setEditCaption("");
    };
    const saveEdit = async (id) => {
        setSavingCaption(true);
        try {
            await updateImage(id, { caption: editCaption });
            setImages((prev) => prev.map((x) => x.id === id ? { ...x, caption: editCaption.trim().slice(0, 500) } : x));
            setEditingId(null);
            setEditCaption("");
            toast.success("Caption saved");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSavingCaption(false);
        }
    };

    const load = async () => {
        setLoading(true);
        try {
            const [imgs, s] = await Promise.all([listImages(activeGardenId || undefined), fetchBillingStatus()]);
            setImages(imgs);
            setStatus(s);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeGardenId]);

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        setUpgradeMsg(null);
        try {
            await uploadImage(file, { caption, gardenId: activeGardenId });
            toast.success("Photo uploaded");
            setCaption("");
            if (fileRef.current) fileRef.current.value = "";
            await load();
        } catch (err) {
            const msg = upgradeMessageFrom(err);
            if (msg) setUpgradeMsg(msg);
            else toast.error(formatApiError(err));
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this photo?")) return;
        try {
            await deleteImage(id);
            toast.success("Photo removed");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const preview = async (id) => {
        try {
            const full = await getImage(id);
            setPreviewUri(full.data_uri);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const usage = status?.usage?.images ?? 0;
    const cap = status?.limits?.images;
    const quotaLabel = cap == null ? `${usage} photos · unlimited` : `${usage} / ${cap} photos used`;

    return (
        <div className="space-y-8" data-testid="images-page">
            <PageHeader
                overline="Photo journal"
                title="Garden photos"
                description="Upload snapshots of your beds, crops, harvests. Free plan allows 5 photos · Plus allows 100."
                action={
                    <Button asChild variant="outline" className="rounded-full border-[var(--vm-border)]" data-testid="images-pricing-link">
                        <Link to="/pricing"><Sparkles className="w-4 h-4 mr-2" />See Plus</Link>
                    </Button>
                }
            />

            {upgradeMsg && <UpgradeBanner message={upgradeMsg} />}

            {/* Upload card */}
            <section className="journal-card p-6" data-testid="upload-card">
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[var(--vm-accent)]/10 flex items-center justify-center shrink-0">
                        <Camera className="w-6 h-6 text-[var(--vm-accent)]" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 space-y-3">
                        <div>
                            <h3 className="font-serif-display text-2xl text-[var(--vm-text)]">Add a photo</h3>
                            <p className="text-xs text-[var(--vm-text-muted)] mt-1">{quotaLabel}</p>
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="caption" className="text-xs uppercase tracking-wider text-[var(--vm-sage)]">Caption (optional)</Label>
                            <Input
                                id="caption"
                                data-testid="image-caption-input"
                                value={caption}
                                onChange={(e) => setCaption(e.target.value)}
                                placeholder="e.g. Tomato seedlings, 4 weeks old"
                                className="bg-[var(--vm-bg)] border-[var(--vm-border)]"
                            />
                        </div>
                        <div>
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleUpload}
                                data-testid="image-file-input"
                            />
                            <Button
                                onClick={() => fileRef.current?.click()}
                                disabled={uploading}
                                data-testid="image-upload-btn"
                                className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]"
                            >
                                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                                {uploading ? "Uploading…" : "Choose photo"}
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Gallery */}
            <section>
                {loading ? (
                    <div className="flex items-center gap-2 text-[var(--vm-text-muted)]">
                        <Loader2 className="w-4 h-4 animate-spin" /> loading photos…
                    </div>
                ) : images.length === 0 ? (
                    <div className="journal-card p-10 text-center" data-testid="images-empty">
                        <ImageIcon className="w-10 h-10 mx-auto text-[var(--vm-text-muted)] opacity-50 mb-3" strokeWidth={1.5} />
                        <p className="text-[var(--vm-text-secondary)]">No photos yet — upload your first snapshot above.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="images-grid">
                        {images.map((img) => (
                            <div key={img.id} className="journal-card overflow-hidden group" data-testid={`image-card-${img.id}`}>
                                <button
                                    onClick={() => preview(img.id)}
                                    className="relative w-full aspect-square bg-[var(--vm-card)] flex items-center justify-center overflow-hidden"
                                >
                                    <ImageIcon className="w-10 h-10 text-[var(--vm-text-muted)] opacity-30" strokeWidth={1.5} />
                                    <span className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-[var(--vm-bg)]/60 flex items-center justify-center transition">
                                        <span className="text-xs text-[var(--vm-text)] font-medium flex items-center gap-1">
                                            Preview <ArrowRight className="w-3 h-3" />
                                        </span>
                                    </span>
                                </button>
                                <div className="p-3 space-y-2">
                                    {editingId === img.id ? (
                                        <div className="space-y-2" data-testid={`caption-editor-${img.id}`}>
                                            <textarea
                                                value={editCaption}
                                                onChange={(e) => setEditCaption(e.target.value)}
                                                maxLength={500}
                                                rows={2}
                                                autoFocus
                                                data-testid={`caption-input-${img.id}`}
                                                className="w-full text-xs bg-[var(--vm-input)] border border-[var(--vm-border)] rounded-md p-2 resize-none focus:outline-none focus:border-[var(--vm-accent)]"
                                                placeholder="What's growing here?"
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) saveEdit(img.id);
                                                    if (e.key === "Escape") cancelEdit();
                                                }}
                                            />
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => saveEdit(img.id)}
                                                    disabled={savingCaption}
                                                    data-testid={`save-caption-${img.id}`}
                                                    className="flex-1 text-xs flex items-center justify-center gap-1 py-1 rounded-md bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)] disabled:opacity-50"
                                                >
                                                    <Check className="w-3 h-3" /> Save
                                                </button>
                                                <button
                                                    onClick={cancelEdit}
                                                    disabled={savingCaption}
                                                    data-testid={`cancel-caption-${img.id}`}
                                                    className="flex-1 text-xs flex items-center justify-center gap-1 py-1 rounded-md border border-[var(--vm-border)] text-[var(--vm-text-muted)] hover:bg-[var(--vm-input)]"
                                                >
                                                    <X className="w-3 h-3" /> Cancel
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex items-start justify-between gap-2">
                                            <button
                                                type="button"
                                                onClick={() => startEdit(img)}
                                                data-testid={`caption-display-${img.id}`}
                                                className="text-left text-xs text-[var(--vm-text-secondary)] line-clamp-2 flex-1 cursor-text hover:text-[var(--vm-text)] transition-colors"
                                                title="Click to edit caption"
                                            >
                                                {img.caption || <span className="italic opacity-60 flex items-center gap-1"><Pencil className="w-3 h-3" /> Add a caption…</span>}
                                            </button>
                                            <div className="flex items-center gap-1 shrink-0">
                                                <button
                                                    onClick={() => openDiagnose(img)}
                                                    data-testid={`diagnose-image-${img.id}`}
                                                    className="text-[var(--vm-text-muted)] hover:text-[var(--vm-accent)] p-1 rounded"
                                                    aria-label="Diagnose with rooteed"
                                                    title="Diagnose with rooteed"
                                                >
                                                    <Sparkles className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => startEdit(img)}
                                                    data-testid={`edit-caption-${img.id}`}
                                                    className="text-[var(--vm-text-muted)] hover:text-[var(--vm-accent)] p-1 rounded"
                                                    aria-label="Edit caption"
                                                >
                                                    <Pencil className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(img.id)}
                                                    data-testid={`delete-image-${img.id}`}
                                                    className="text-[var(--vm-text-muted)] hover:text-[var(--vm-secondary)] p-1 rounded"
                                                    aria-label="Delete photo"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Lightbox */}
            {previewUri && (
                <div
                    onClick={() => setPreviewUri(null)}
                    className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6 cursor-zoom-out"
                    data-testid="image-lightbox"
                >
                    <img src={previewUri} alt="Garden preview" className="max-w-full max-h-full object-contain rounded-md shadow-2xl" />
                </div>
            )}

            <DiagnoseDialog
                open={!!diagnoseTarget}
                onClose={() => setDiagnoseTarget(null)}
                source={diagnoseTarget || {}}
                mode="diagnose"
            />
        </div>
    );
}
